import React, { useState } from 'react';
import {
    Box,
    Image,
    IconButton,
    Text,
    Center,
    useColorModeValue,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { getFullImageUrl } from '../utils';

interface ImageCarouselProps {
    pictures: string[];
    placeholderText?: string;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({ 
    pictures, 
    placeholderText = "Tidak ada foto" 
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    const hasImages = pictures && pictures.length > 0;
    const fullUrls = (pictures || []).map(getFullImageUrl);

    const handlePrev = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === 0 ? fullUrls.length - 1 : prev - 1));
    };

    const handleNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === fullUrls.length - 1 ? 0 : prev + 1));
    };

    const carouselBg = useColorModeValue('gray.100', 'gray.700');
    const placeholderColor = useColorModeValue('gray.400', 'gray.500');
    const emptyBorderColor = useColorModeValue('gray.300', 'gray.600');

    if (!hasImages) {
        return (
            <Center 
                height="200px" 
                bg={carouselBg} 
                borderRadius="md" 
                border="1px dashed" 
                borderColor={emptyBorderColor}
            >
                <Text color={placeholderColor} fontSize="sm">{placeholderText}</Text>
            </Center>
        );
    }

    return (
        <Box 
            position="relative" 
            height="200px" 
            bg={carouselBg} 
            borderRadius="md" 
            overflow="hidden"
        >
            <Image
                src={fullUrls[currentIndex]}
                alt={`Image ${currentIndex + 1}`}
                height="200px"
                width="100%"
                objectFit="contain"
                cursor="zoom-in"
                onClick={() => setIsZoomed(true)}
            />

            {fullUrls.length > 1 && (
                <>
                    <IconButton
                        aria-label="Previous image"
                        icon={<ChevronLeftIcon />}
                        size="sm"
                        position="absolute"
                        left="8px"
                        top="50%"
                        transform="translateY(-50%)"
                        onClick={handlePrev}
                        opacity={0.7}
                        _hover={{ opacity: 1 }}
                        zIndex={2}
                        colorScheme="blackAlpha"
                        borderRadius="full"
                    />
                    <IconButton
                        aria-label="Next image"
                        icon={<ChevronRightIcon />}
                        size="sm"
                        position="absolute"
                        right="8px"
                        top="50%"
                        transform="translateY(-50%)"
                        onClick={handleNext}
                        opacity={0.7}
                        _hover={{ opacity: 1 }}
                        zIndex={2}
                        colorScheme="blackAlpha"
                        borderRadius="full"
                    />
                    <Box
                        position="absolute"
                        bottom="8px"
                        left="50%"
                        transform="translateX(-50%)"
                        bg="blackAlpha.700"
                        color="white"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                        fontSize="xs"
                        pointerEvents="none"
                        zIndex={2}
                    >
                        {currentIndex + 1} / {fullUrls.length}
                    </Box>
                </>
            )}

            {/* Custom Fullscreen Zoom Gallery Modal */}
            <Modal isOpen={isZoomed} onClose={() => setIsZoomed(false)} size="full" isCentered>
                <ModalOverlay bg="blackAlpha.900" />
                <ModalContent bg="transparent" boxShadow="none">
                    <ModalCloseButton color="white" size="lg" zIndex={10} />
                    <ModalBody 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="center" 
                        p={0} 
                        position="relative" 
                        onClick={() => setIsZoomed(false)}
                    >
                        {/* Zoomed Large Image */}
                        <Image
                            src={fullUrls[currentIndex]}
                            alt={`Zoomed image ${currentIndex + 1}`}
                            maxH="90vh"
                            maxW="90vw"
                            objectFit="contain"
                            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking the image itself
                        />

                        {/* Navigation inside Zoom - Previous */}
                        {fullUrls.length > 1 && (
                            <IconButton
                                aria-label="Previous image zoomed"
                                icon={<ChevronLeftIcon w={8} h={8} />}
                                size="lg"
                                position="absolute"
                                left="24px"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrev(e);
                                }}
                                colorScheme="blackAlpha"
                                color="white"
                                borderRadius="full"
                                _hover={{ bg: "blackAlpha.800" }}
                            />
                        )}

                        {/* Navigation inside Zoom - Next */}
                        {fullUrls.length > 1 && (
                            <IconButton
                                aria-label="Next image zoomed"
                                icon={<ChevronRightIcon w={8} h={8} />}
                                size="lg"
                                position="absolute"
                                right="24px"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNext(e);
                                }}
                                colorScheme="blackAlpha"
                                color="white"
                                borderRadius="full"
                                _hover={{ bg: "blackAlpha.800" }}
                            />
                        )}

                        {/* Indicator inside Zoom */}
                        {fullUrls.length > 1 && (
                            <Box
                                position="absolute"
                                bottom="24px"
                                bg="blackAlpha.700"
                                color="white"
                                px={3}
                                py={1}
                                borderRadius="md"
                                fontSize="sm"
                            >
                                {currentIndex + 1} / {fullUrls.length}
                            </Box>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};
